import { useEffect, useState } from "react";
import { Typography, Box, TextField } from "@mui/material";
import PropTypes from "prop-types";
import Tile from "components/Tile.jsx";
import Button from "@accredo/fe-shared/theme/components/Button.jsx";
import { backendPut } from "@accredo/fe-shared/services/dataService.js";
import { useSnackbar } from "notistack";

/**
 * Kundeneinstellung für Kunde::object_ref_num_label (ACC-297): das Label, mit
 * dem Objekt::customer_reference_number überall in der UI beschriftet wird
 * (Objekt-Import, Objekt-/Bankkonto-Tabellen, Controlling, Schaden-Details -
 * siehe docs/features/object-import.md, Abschnitt 4d), statt des generischen
 * Fallbacks "Ref-ID".
 */
export default function CustomerObjectRefNumLabelSetting({
  customerId,
  label,
  onUpdate = () => {},
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);
  const [value, setValue] = useState(label ?? "");

  useEffect(() => {
    setValue(label ?? "");
  }, [label]);

  const handleSave = () => {
    const trimmed = value.trim();
    backendPut(`data/kundes/${customerId}/object-ref-num-label`, {
      toSend: { object_ref_num_label: trimmed || null },
      setIsLoading: setIsSaving,
      setResult: () => onUpdate(),
      successMessage: "Bezeichnung wurde gespeichert.",
      label: "Ref-ID-Label",
      enqueueSnackbar,
    });
  };

  const hasChanges = value.trim() !== (label ?? "");

  return (
    <Tile sx={{ mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        Bezeichnung für Referenznummer
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Legt für diesen Kunden eine eigene Bezeichnung für die Referenznummer
        seiner Objekte fest (z. B. „Wirtschaftseinheit" oder „Gebäude-Nr.").
        Diese Bezeichnung erscheint anstelle des Standardbegriffs „Ref-ID" u.
        a. beim Objekt-Import (Spaltenüberschrift und Matching-Hinweis), in
        den Objekt- und Bankkonto-Tabellen, im Controlling-Export und in den
        Schaden-Details. Ist keine Bezeichnung gesetzt, gilt der Kunde
        fachlich als „führt keine Referenznummern" — die Spalte entfällt dann
        an den meisten Stellen komplett, statt einen Platzhalter zu zeigen.
      </Typography>

      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", maxWidth: 480 }}>
        <TextField
          label="Bezeichnung"
          placeholder="z. B. Wirtschaftseinheit"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          disabled={isSaving}
          fullWidth
          size="small"
        />
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isSaving || !hasChanges}
        >
          Speichern
        </Button>
      </Box>
    </Tile>
  );
}

CustomerObjectRefNumLabelSetting.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  label: PropTypes.string,
  onUpdate: PropTypes.func,
};
